/**
 * Doubao Seedream 5.0 图片生成（经后端 /volc_seedream_image 代理）
 * 返回结构通常为：{ created, data: [{ url }...] }
 */

import { resolveApiUrl } from './apiBaseUrl'

/** 与 FastMovieController volc_seedream_image 校验上限对齐（英文电商提示词易超长） */
export const SEEDREAM_PROMPT_MAX_CHARS = 7800

export function normalizeSeedreamPrompt(prompt) {
  let t = prompt == null ? '' : String(prompt).trim()
  if (!t) t = 'Professional e-commerce product marketing image, high quality'
  if (t.length > SEEDREAM_PROMPT_MAX_CHARS) {
    t = t.slice(0, SEEDREAM_PROMPT_MAX_CHARS)
  }
  return t
}

function getAuthHeader() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || ''
  return token || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function unwrapPayload(res) {
  // 兼容：Laravel 再包一层 { data: { created, data: [...] } }；不能把「本身就是图片数组」的 data 当成要剥掉的外壳
  if (!res || typeof res !== 'object' || Array.isArray(res)) return res
  let r = res
  if (
    r.data &&
    typeof r.data === 'object' &&
    !Array.isArray(r.data) &&
    (Array.isArray(r.data.data) || Array.isArray(r.data.images))
  ) {
    r = r.data
  }
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data) && Array.isArray(r.data.data)) {
    r = { ...r, data: r.data.data }
  }
  return r
}

/**
 * 从 Seedream /volc_seedream_image 响应中取第一张图 URL（与 ConversationWorkflow pickSeedreamImageUrls 行为对齐）
 * @param {any} out
 * @returns {string}
 */
export function pickFirstSeedreamImageUrl(out) {
  const urls = pickSeedreamImageUrls(out)
  return urls.length ? urls[0] : ''
}

function pickSeedreamImageUrls(out) {
  const urls = []
  const dataArr = Array.isArray(out?.data)
    ? out.data
    : Array.isArray(out?.result)
      ? out.result
      : Array.isArray(out?.images)
        ? out.images
        : Array.isArray(out?.data?.data)
          ? out.data.data
          : Array.isArray(out)
            ? out
            : []
  for (const row of dataArr || []) {
    if (!row) continue
    if (typeof row === 'string' && row.trim()) {
      urls.push(row.trim())
      continue
    }
    const u =
      (typeof row.public_url === 'string' && row.public_url.trim() ? row.public_url.trim() : '') ||
      (typeof row.url === 'string' && row.url.trim() ? row.url.trim() : '') ||
      (typeof row.image_url === 'string' && row.image_url.trim() ? row.image_url.trim() : '') ||
      (typeof row.imageUrl === 'string' && row.imageUrl.trim() ? row.imageUrl.trim() : '') ||
      (typeof row.upstream_url === 'string' && row.upstream_url.trim() ? row.upstream_url.trim() : '')
    if (u) {
      urls.push(u)
      continue
    }
    const b64 =
      (typeof row.b64_json === 'string' && row.b64_json.trim() ? row.b64_json.trim() : '') ||
      (typeof row.b64 === 'string' && row.b64.trim() ? row.b64.trim() : '') ||
      ''
    if (b64) {
      urls.push('data:image/png;base64,' + b64)
    }
  }
  return urls
}

/**
 * @param {Object} opts
 * @param {string} opts.prompt
 * @param {string} [opts.model] 例如 doubao-seedream-5-0-260128
 * @param {string} [opts.size] 例如 2048x2048 / 2K / 4K（以控制台与接口为准）
 * @param {number} [opts.n] 1-8
 * @param {'url'|'b64_json'} [opts.response_format]
 * @param {boolean} [opts.watermark]
 * @param {number} [opts.seed]
 * @param {string} [opts.mask] GPT-Image-2 局部重绘蒙版 URL / dataURL
 * @param {AbortSignal} [opts.signal]
 */
export async function generateSeedreamImages(opts) {
  const images =
    Array.isArray(opts?.images) ? opts.images.map(x => (x == null ? '' : String(x).trim())).filter(Boolean) : []
  const payload = {
    prompt: normalizeSeedreamPrompt(opts?.prompt),
    ...(opts?.model ? { model: String(opts.model) } : {}),
    ...(opts?.size ? { size: String(opts.size) } : {}),
    ...(opts?.n != null ? { n: Number(opts.n) } : {}),
    ...(opts?.response_format ? { response_format: String(opts.response_format) } : {}),
    ...(opts?.watermark != null ? { watermark: !!opts.watermark } : {}),
    ...(opts?.seed != null ? { seed: Number(opts.seed) } : {}),
    ...(opts?.image ? { image: String(opts.image) } : {}),
    ...(images.length ? { images } : {}),
    ...(opts?.mask != null && String(opts.mask).trim()
      ? { mask: String(opts.mask).trim() }
      : {})
  }

  let res
  try {
    res = await fetch(resolveApiUrl('/volc_seedream_image'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAuthHeader()
      },
      body: JSON.stringify(payload),
      signal: opts?.signal
    })
  } catch (e) {
    const m = e && e.message ? String(e.message) : String(e)
    if (/failed to fetch|networkerror|load failed/i.test(m)) {
      throw new Error(
        '出图请求中断（常见于代理/网关超时：GPT-Image-2 较慢，需 2～5 分钟）。请重试；本机开发请确认 vue.config 里 /volc_seedream_image 已配置长超时代理；线上请在 Nginx 等提高 proxy_read_timeout。'
      )
    }
    throw e instanceof Error ? e : new Error(m)
  }

  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.msg || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return unwrapPayload(data)
}

